import { useState, useEffect, useCallback, useRef } from 'react';
import { cacheRead, cacheWrite, cacheInvalidate, cacheAge } from './dataCache';
import { getCurrentUserId } from './currentUser';

// ── Config ──────────────────────────────────────────────────
const HARD_TTL_MS   = 5 * 60 * 1000;  // 5 min — show full loading if older
const REVALIDATE_MS = 60 * 1000;       // 60 s — silently revalidate if older

// ── Debounce hook ───────────────────────────────────────────
export function useDebounce<T>(value: T, delayMs = 350): T {
  const [dv, setDv] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return dv;
}

// ── formatAge helper ─────────────────────────────────────────
export function formatAge(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  return `${Math.round(ms / 60_000)}m ago`;
}

// ── Toast ────────────────────────────────────────────────────
export interface ToastState {
  msg: string;
  type: 'success' | 'error';
}

export function useToast() {
  const [toast, setToast] = useState<ToastState | null>(null);
  const show = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);
  return { toast, showToast: show };
}

// ── Main SWR hook ────────────────────────────────────────────
export interface UseAdminPageOptions<T> {
  /** Cache namespace prefix, e.g. 'bookings:' */
  cacheNs: string;
  /** Fetcher function — receives the filter values */
  fetcher: () => Promise<T>;
  /** Build cache key suffix from current filters */
  cacheKeySuffix: string;
}

export interface UseAdminPageResult<T> {
  data: T;
  loading: boolean;
  revalidating: boolean;
  lastSyncedAt: number | null;
  syncLabel: string;
  /** Hard reload with loading spinner */
  refresh: () => Promise<void>;
  /** Silent background revalidation */
  revalidate: () => Promise<void>;
  /** Invalidate cache and hard reload — call after mutations */
  mutate: () => Promise<void>;
}

export function useAdminPage<T>(
  emptyResult: T,
  options: UseAdminPageOptions<T>,
): UseAdminPageResult<T> {
  const { cacheNs, fetcher, cacheKeySuffix } = options;
  const userId = getCurrentUserId() || 'anon';
  const key = `${cacheNs}${cacheKeySuffix}:${userId}`;

  const [data, setData]                 = useState<T>(emptyResult);
  const [loading, setLoading]           = useState(true);
  const [revalidating, setRevalidating] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [syncLabel, setSyncLabel]       = useState('');

  // Keep fetcher ref stable to avoid re-render loops
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Tick "synced X ago"
  useEffect(() => {
    if (!lastSyncedAt) return;
    const tick = () => setSyncLabel(formatAge(Date.now() - lastSyncedAt));
    tick();
    const id = setInterval(tick, 15_000);
    return () => clearInterval(id);
  }, [lastSyncedAt]);

  // Core fetch
  const doFetch = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRevalidating(true);

    try {
      const result = await fetcherRef.current();
      cacheWrite(key, result);
      setData(result);
      setLastSyncedAt(Date.now());
      setSyncLabel('just now');
    } catch (e) {
      console.error(`[useAdminPage] fetch error for ${cacheNs}:`, e);
    }

    if (!silent) setLoading(false);
    else setRevalidating(false);
  }, [key, cacheNs]);

  // On mount / filter change — SWR logic
  useEffect(() => {
    const cached = cacheRead<T>(key, HARD_TTL_MS);
    if (cached) {
      setData(cached);
      setLoading(false);
      const age = cacheAge(key) ?? 0;
      setLastSyncedAt(Date.now() - age);
      if (age > REVALIDATE_MS) {
        doFetch(true);
      }
      return;
    }
    doFetch(false);
  }, [key, doFetch]);

  const refresh = useCallback(async () => {
    cacheInvalidate(cacheNs);
    await doFetch(false);
  }, [cacheNs, doFetch]);

  const revalidate = useCallback(async () => {
    await doFetch(true);
  }, [doFetch]);

  const mutate = useCallback(async () => {
    cacheInvalidate(cacheNs);
    await doFetch(false);
  }, [cacheNs, doFetch]);

  return { data, loading, revalidating, lastSyncedAt, syncLabel, refresh, revalidate, mutate };
}
